import type React from 'react';
import {
	DocumentsIcon,
	ExcelIcon,
	ImageIcon,
	PdfIcon,
	WordIcon,
} from '@/assets/icons';

export type FileIconType = React.ComponentType<{
	width?: number;
	height?: number;
}>;

/**
 * Get the appropriate icon component based on file type/extension
 * @param fileName - The name of the file
 * @param fileType - The MIME type of the file
 * @returns The appropriate icon component
 */
export const getFileIcon = (
	fileName: string,
	fileType?: string,
): FileIconType => {
	// Get file extension (handle edge cases)
	const parts = fileName.toLowerCase().split('.');
	const extension = parts.length > 1 ? parts[parts.length - 1] : '';

	// PDF files
	if (extension === 'pdf') {
		return PdfIcon;
	}

	// Image files
	if (
		extension === 'jpg' ||
		extension === 'jpeg' ||
		extension === 'png' ||
		extension === 'gif' ||
		extension === 'svg' ||
		extension === 'webp' ||
		extension === 'bmp'
	) {
		return ImageIcon;
	}

	// Excel/Spreadsheet files
	if (
		extension === 'xls' ||
		extension === 'xlsx' ||
		extension === 'xlsm' ||
		extension === 'xlsb' ||
		extension === 'csv'
	) {
		return ExcelIcon;
	}

	// Word document files
	if (
		extension === 'doc' ||
		extension === 'docx' ||
		extension === 'docm' ||
		extension === 'dot' ||
		extension === 'dotx'
	) {
		return WordIcon;
	}

	// Fallback: check by MIME type if extension didn't match
	if (fileType) {
		if (fileType.includes('pdf')) {
			return PdfIcon;
		}
		if (fileType.includes('image')) {
			return ImageIcon;
		}
		if (
			fileType.includes('spreadsheet') ||
			fileType.includes('excel') ||
			fileType.includes('csv')
		) {
			return ExcelIcon;
		}
		if (fileType.includes('word') || fileType.includes('document')) {
			return WordIcon;
		}
	}

	// Default fallback: generic document icon
	return DocumentsIcon;
};

/**
 * Extracts document name from SharePoint URL or file path
 * Example: "https://...../BV/Churchill-2032-06-10/" => "Churchill-2032-06-10"
 * @param url - The SharePoint URL or file path
 * @returns The extracted document/file name
 */
export const extractDocumentName = (url: string): string => {
	if (!url) return '-';

	// Remove trailing slash if present
	const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;

	// Split by '/' and get the last segment
	const segments = cleanUrl.split('/');
	const documentName = segments[segments.length - 1];

	return documentName || '-';
};

/**
 * Converts a File object to base64 string
 * @param file - The file to convert
 * @returns Promise with base64 string (without data URL prefix)
 */
export const fileToBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			const base64String = reader.result as string;
			// Remove the data URL prefix (e.g., "data:image/png;base64,")
			const base64Data = base64String.split(',')[1];
			resolve(base64Data);
		};
		reader.onerror = (error) => reject(error);
	});
};

/**
 * Converts a base64 string to a displayable image URL
 * @param base64String - The base64 string to convert
 * @returns The displayable image URL
 */
export const getDisplayableImageUrl = (
	base64String?: string,
): string | undefined => {
	if (!base64String) return undefined;
	if (base64String.startsWith('data:')) return base64String;
	return `data:image/jpeg;base64,${base64String}`;
};
